/**
 * Cache identity for workspace-file server state.
 *
 * Defined here as typed factories rather than inline arrays so invalidation
 * and prefetching cannot drift from the queries they target. Every key is
 * scoped by workspace, and the listing/content/history keys are further
 * scoped by path — a file's content and history are distinct from its
 * neighbors, and a directory's listing is distinct from any other directory.
 */
import { isInsideFolder } from './folders';

export const fileKeys = {
  all: (workspaceId: string) => ['workspace-files', workspaceId] as const,
  listing: (workspaceId: string, path: string) =>
    ['workspace-files', workspaceId, 'listing', path] as const,
  content: (workspaceId: string, path: string) =>
    ['workspace-files', workspaceId, 'content', path] as const,
  history: (workspaceId: string, path: string) =>
    ['workspace-files', workspaceId, 'history', path] as const,
  /**
   * The whole workspace's change log, unfiltered by path.
   *
   * Deliberately not `history(workspaceId, '')`: an empty path is a real path
   * to the service — the root — and reusing that key would make a folder's
   * history and the workspace feed the same cache entry.
   */
  workspaceHistory: (workspaceId: string) =>
    ['workspace-files', workspaceId, 'workspace-history'] as const,
  /** Everything under a folder, at any depth — read before deleting it. */
  folderContents: (workspaceId: string, path: string) =>
    ['workspace-files', workspaceId, 'folder-contents', path] as const,
  /**
   * Whether `key` is a listing (or folder-contents read) of `folderPath` or of
   * anything beneath it, in this workspace.
   *
   * Needed because a listing key holds its path as one element, so TanStack
   * Query's prefix match can never express "every folder under `docs`" —
   * `listing(ws, 'docs')` is not a prefix of `listing(ws, 'docs/sub')`. Moving
   * or renaming a folder makes every cached listing beneath it wrong at once,
   * and this is the predicate that finds them.
   */
  isUnderFolder: (
    key: readonly unknown[],
    workspaceId: string,
    folderPath: string,
  ): boolean => {
    const [scope, ws, kind, path] = key;
    return (
      scope === 'workspace-files' &&
      ws === workspaceId &&
      (kind === 'listing' || kind === 'folder-contents') &&
      typeof path === 'string' &&
      (path === folderPath || isInsideFolder(path, folderPath))
    );
  },
};
